import { html, unsafeCSS } from 'lit';
import { property, query } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { DomUtility } from '~/ui/utils';

import { styles as globalStyles } from '~/ui/scss/main.styles';
import DERFormElement from '~/ui/utils/der-form-element';
import { styles } from './dropdown.styles';

export interface DropdownItem {
    name: string;
    id: string;
}

export type DropdownVariant = 'default' | 'exclusive';

export class Dropdown extends DERFormElement {
    static override styles = [unsafeCSS(globalStyles), unsafeCSS(styles)];

    @query('select') selectEl?: HTMLSelectElement;
    @property({ type: String }) selectId;
    @property({ type: String }) label?: string;
    @property({ type: String }) name = '';
    @property({ type: String }) placeholder = '';
    @property({ type: String, attribute: 'optional-label' }) optionalLabel? = '(Optional)';
    @property({ type: Array }) options: DropdownItem[] = [];
    @property({ type: String }) variant: DropdownVariant = 'default';
    @property({ type: Boolean }) required = false;
    @property({ type: String, reflect: true }) set value(val: string) {
        this._value = val;
        this._internals.setFormValue(this.value);
    }
    get value() {
        return this._value;
    }
    @property({ type: Boolean }) disabled = false;
    @property({ type: Boolean }) combined = false;
    @property({ type: Boolean, reflect: true }) invalid = false;
    @property({ type: String }) errorMessage? = 'Error Message';

    private _value = '';

    constructor() {
        super();
        this.selectId = DomUtility.uniqueElementId(this);
    }

    get validity() {
        return this.selectEl?.validity;
    }

    get validationMessage() {
        return this.selectEl?.validationMessage;
    }

    override connectedCallback() {
        super.connectedCallback();
        this.updateComplete.then(() => this.checkInputValidity());
    }

    override updated(changedProps: Map<keyof this, unknown>) {
        if (changedProps.has('value') && this.selectEl) {
            this.selectEl.value = this.value;
            this.checkInputValidity();
            this.emitEvent('rendered', {});
        }
    }

    override emitEvent(name: string, detail: unknown): void {
        this.dispatchEvent(new CustomEvent(name, { detail, bubbles: true, composed: true }));
    }

    onBlur(event: FocusEvent): void {
        this.emitEvent('der.blur', event);
    }

    override firstUpdated() {
        if (this.value) {
            this.emitEvent('der.change', { value: this.value });
        }
    }

    onChange(event: Event): void {
        const target = event.target as HTMLSelectElement;
        this.value = target.value;
        this.checkInputValidity();
        this.emitEvent('der.change', { value: this.value, event });
    }

    onFocus(event: FocusEvent): void {
        this.emitEvent('der.focus', event);
    }

    private optionsMapper = ({ name, id }: DropdownItem) => {
        return html` <option class="c-dropdown__option" value="${id}">${name}</option> `;
    };

    private renderSelect = () => {
        return html`
            <select
                class=${classMap({
                    'c-dropdown__select': true,
                    combined: this.combined,
                })}
                data-dropdown-style-variant="${this.variant}"
                id="${this.selectId}"
                name="${this.name}"
                aria-required="${this.required}"
                ?disabled="${this.disabled}"
                @change="${this.onChange}"
                @blur="${this.onBlur}"
                @focus="${this.onFocus}"
                ?required="${this.required}"
            >
                ${this.placeholder
                    ? html`<option class="c-dropdown__option" value="">${this.placeholder}</option>`
                    : ''}
                ${this.options.map((option) => this.optionsMapper(option))}
            </select>
        `;
    };

    private renderExclusiveIcon = () => {
        return html`<i class=" c-dropdown__select-icon c-dropdown__select-icon--before">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                <path
                    d="M6.4 19H17.575L15.475 16.9C14.9917 17.25 14.4542 17.5208 13.8625 17.7125C13.2708 17.9042 12.65 18 12 18C11.35 18 10.7292 17.9 10.1375 17.7C9.54583 17.5 9.00833 17.225 8.525 16.875L6.4 19ZM5 17.575L7.1 15.475C6.75 14.9917 6.47917 14.4542 6.2875 13.8625C6.09583 13.2708 6 12.65 6 12C6 11.35 6.1 10.7292 6.3 10.1375C6.5 9.54583 6.775 9.00833 7.125 8.525L5 6.4V17.575ZM8.55 14.025L10.6 12L8.55 9.975C8.36667 10.275 8.22917 10.5917 8.1375 10.925C8.04583 11.2583 8 11.6167 8 12C8 12.3833 8.04583 12.7417 8.1375 13.075C8.22917 13.4083 8.36667 13.725 8.55 14.025ZM12 16C12.3833 16 12.7417 15.9542 13.075 15.8625C13.4083 15.7708 13.725 15.6333 14.025 15.45L12 13.4L9.95 15.45C10.25 15.6333 10.5708 15.7708 10.9125 15.8625C11.2542 15.9542 11.6167 16 12 16ZM12 10.575L14.025 8.55C13.725 8.36667 13.4083 8.22917 13.075 8.1375C12.7417 8.04583 12.3833 8 12 8C11.6167 8 11.2583 8.04583 10.925 8.1375C10.5917 8.22917 10.275 8.36667 9.975 8.55L12 10.575ZM15.45 14.025C15.6333 13.725 15.7708 13.4083 15.8625 13.075C15.9542 12.7417 16 12.3833 16 12C16 11.6167 15.9542 11.2542 15.8625 10.9125C15.7708 10.5708 15.6333 10.25 15.45 9.95L13.425 11.975L15.45 14.025ZM19 17.575V6.4L16.875 8.525C17.225 9.00833 17.5 9.54583 17.7 10.1375C17.9 10.7292 18 11.35 18 12C18 12.65 17.9042 13.2708 17.7125 13.8625C17.5208 14.4542 17.25 14.9917 16.9 15.475L19 17.575ZM15.475 7.125L17.6 5H6.425L8.525 7.1C9.00833 6.75 9.54583 6.47917 10.1375 6.2875C10.7292 6.09583 11.35 6 12 6C12.65 6 13.2708 6.1 13.8625 6.3C14.4542 6.5 14.9917 6.775 15.475 7.125ZM5 21C4.45 21 3.97917 20.8042 3.5875 20.4125C3.19583 20.0208 3 19.55 3 19V5C3 4.45 3.19583 3.97917 3.5875 3.5875C3.97917 3.19583 4.45 3 5 3H19C19.55 3 20.0208 3.19583 20.4125 3.5875C20.8042 3.97917 21 4.45 21 5V19C21 19.55 20.8042 20.0208 20.4125 20.4125C20.0208 20.8042 19.55 21 19 21H5Z"
                />
            </svg>
        </i>`;
    };

    private renderDropDownIcon = () => {
        return html`<i class="c-dropdown__select-icon c-dropdown__select-icon--after">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                <g clip-path="url(#clip0_114_248)">
                    <mask
                        id="mask0_114_248"
                        style="mask-type:alpha"
                        maskUnits="userSpaceOnUse"
                        x="0"
                        y="0"
                        width="24"
                        height="24"
                    >
                        <rect width="24" height="24" />
                    </mask>
                    <g mask="url(#mask0_114_248)">
                        <path d="M12 15.4L6 9.39999L7.18325 8.21674L12 13.0332L16.8167 8.21674L18 9.39999L12 15.4Z" />
                    </g>
                </g>
                <defs>
                    <clipPath id="clip0_114_248">
                        <rect width="24" height="24" />
                    </clipPath>
                </defs>
            </svg>
        </i>`;
    };

    private renderDropDownInvalidIcon = () => {
        return html`<i class="c-dropdown__invalid-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 40 40">
                <g id="variant=attention_full_circle">
                    <path
                        id="Vector"
                        d="M20 28C20.4533 28 20.8333 27.8467 21.14 27.54C21.4467 27.2333 21.6 26.8533 21.6 26.4C21.6 25.9467 21.4467 25.5667 21.14 25.26C20.8333 24.9533 20.4533 24.8 20 24.8C19.5467 24.8 19.1667 24.9533 18.86 25.26C18.5533 25.5667 18.4 25.9467 18.4 26.4C18.4 26.8533 18.5533 27.2333 18.86 27.54C19.1667 27.8467 19.5467 28 20 28ZM18.4 21.6H21.6V12H18.4V21.6ZM20 36C17.7867 36 15.7067 35.58 13.76 34.74C11.8133 33.9 10.12 32.76 8.68 31.32C7.24 29.88 6.1 28.1867 5.26 26.24C4.42 24.2933 4 22.2133 4 20C4 17.7867 4.42 15.7067 5.26 13.76C6.1 11.8133 7.24 10.12 8.68 8.68C10.12 7.24 11.8133 6.1 13.76 5.26C15.7067 4.42 17.7867 4 20 4C22.2133 4 24.2933 4.42 26.24 5.26C28.1867 6.1 29.88 7.24 31.32 8.68C32.76 10.12 33.9 11.8133 34.74 13.76C35.58 15.7067 36 17.7867 36 20C36 22.2133 35.58 24.2933 34.74 26.24C33.9 28.1867 32.76 29.88 31.32 31.32C29.88 32.76 28.1867 33.9 26.24 34.74C24.2933 35.58 22.2133 36 20 36Z"
                    />
                </g>
            </svg>
        </i>`;
    };

    private checkInputValidity() {
        if (!this.selectEl) {
            return;
        }
        this.setDERValidity(this.selectEl?.validity, this.selectEl?.validationMessage, this.selectEl);
    }

    override render() {
        return html`
            <div class="c-dropdown__wrapper ">
                <!--default Label start -->

                ${this.variant === 'default' && this.label
                    ? html`
                          <div class="c-dropdown__label-wrapper">
                              <span class="c-dropdown__label ${this.disabled ? 'disabled' : ''}">${this.label}</span>
                              ${!this.required && !this.disabled
                                  ? html` <span class="c-dropdown__optional-label">${this.optionalLabel}</span>`
                                  : ''}
                          </div>
                      `
                    : ''}
                <!--default Label end -->

                <div
                    class="c-dropdown__select-wrapper  ${classMap({
                        disabled: this.disabled,
                        combined: this.combined,
                        invalid: this.invalid,
                        filled: this.value !== '',
                        exclusive: this.variant === 'exclusive',
                    })}"
                    data-value="${this.value || ''}"
                >
                    ${this.variant === 'exclusive'
                        ? html`
                              ${this.renderExclusiveIcon()}
                              <div class="c-dropdown__select-label-wrapper--exclusive ">
                                  <span class="c-dropdown__label ">${this.label}</span>
                                  ${this.renderSelect()}
                              </div>
                          `
                        : html` ${this.renderSelect()} `}
                    ${this.renderDropDownIcon()}
                </div>
                ${this.invalid && this.errorMessage
                    ? html`
                          <span class="c-dropdown__invalid"
                              >${this.renderDropDownInvalidIcon()} ${this.errorMessage}</span
                          >
                      `
                    : ''}
            </div>
        `;
    }
}
